/**
 * geo: pure geography helpers for the capacity map.
 *
 * Straight-line (haversine) distance in miles, and a ranking of any set of
 * home-based items by distance from a placed pin ("place a client"),
 * skipping items with no home point on file. County coverage is a
 * separate pure lookup: which unit(s) — per src/lib/api.ts's
 * UnitCoverageRow shape (GET /units/coverage) — list a given county
 * display name among the counties they cover.
 *
 * trimSegmentPx is a different kind of geometry: pixel-space, not
 * lat/lng — it shortens a drawn line by a radius at each end, used by
 * LeafletMap to pull the "lines to clients" distance rays back from a
 * pin's center to its rim.
 */

import type { UnitCoverageRow } from '@/lib/api';

export interface LatLng {
  lat: number;
  lng: number;
}

/** A point in screen-pixel space (Leaflet layer points, from
 *  map.latLngToLayerPoint) — deliberately not L.Point, so this module
 *  keeps no Leaflet dependency of its own. */
export interface PixelPoint {
  x: number;
  y: number;
}

/**
 * Shortens the segment a→b by `startPx` at the `a` end and `endPx` at the
 * `b` end, moving each endpoint along the line itself. Used to pull a
 * distance ray back from a pin's center to its rim (the pin's own radius
 * plus a small gap) before it's drawn — in whatever pixel space the two
 * points are already given in. This function has no notion of lat/lng,
 * map projection, or zoom; the caller projects with
 * map.latLngToLayerPoint first and unprojects the trimmed result back.
 *
 * Returns null when the segment is too short for both trims to fit (its
 * own length is at or under startPx + endPx) — the two pins' rims already
 * meet or overlap, so no ray is drawn rather than one that runs backwards
 * past its own start.
 */
export function trimSegmentPx(a: PixelPoint, b: PixelPoint, startPx: number, endPx: number): { a: PixelPoint; b: PixelPoint } | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length <= startPx + endPx) return null;
  const ux = dx / length;
  const uy = dy / length;
  return {
    a: { x: a.x + ux * startPx, y: a.y + uy * startPx },
    b: { x: b.x - ux * endPx, y: b.y - uy * endPx },
  };
}

/** Mean Earth radius in miles (the spherical-approximation constant the
 *  geodesy literature uses); plenty precise for "which CM is closest," not
 *  a survey-grade distance. */
const EARTH_RADIUS_MILES = 3958.8;

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance between two points, in miles. */
export function haversineMiles(a: LatLng, b: LatLng): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_MILES * c;
}

export interface DistanceRanked<T> {
  item: T;
  distanceMiles: number;
}

export interface RankByDistanceResult<T> {
  /** Nearest first. */
  ranked: DistanceRanked<T>[];
  /** How many candidates had no home point and were left out of `ranked`. */
  skipped: number;
}

/**
 * Every candidate with a home point, nearest first; a candidate with no
 * home point (`getHome` returns null) is left out of `ranked` and counted
 * in `skipped` instead, so the caller can say how many were skipped.
 */
export function rankByDistance<T>(
  pin: LatLng,
  candidates: readonly T[],
  getHome: (item: T) => LatLng | null | undefined,
): RankByDistanceResult<T> {
  let skipped = 0;
  const ranked: DistanceRanked<T>[] = [];
  for (const item of candidates) {
    const home = getHome(item);
    // No point on file, or none sent: not ranked.
    if (home === null || home === undefined) {
      skipped += 1;
      continue;
    }
    ranked.push({ item, distanceMiles: haversineMiles(pin, home) });
  }
  ranked.sort((a, b) => a.distanceMiles - b.distanceMiles);
  return { ranked, skipped };
}

/**
 * Every unit whose covered counties (UnitCoverageRow.counties, display
 * names) include `countyName`. A county can sit under two units (the
 * shared-county case returns both); a county no unit has claimed returns
 * an empty array — the SAME empty array a null (unresolved) countyName
 * returns, so the caller tells the two apart by countyName's own
 * null-ness, not by this function's result.
 */
export function unitsForCounty(countyName: string | null, unitCoverage: readonly UnitCoverageRow[]): UnitCoverageRow[] {
  if (countyName === null) return [];
  return unitCoverage.filter((u) => u.counties.includes(countyName));
}
